import { Overlay } from '@angular/cdk/overlay';
import { inject, Injectable, signal } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

import { SessionStore } from '../core/services/session.store';
import { useToaster } from '../shared/composables/use-toaster';
import { SiteId } from '../site-detail/models/Site';

import { VideoCall, VideoCallModalData } from './models/video-call';
import { VIDEO_CALL_PROVIDER } from './services/VideoCallProvider';
import { getVideoCallToasterId } from './utils/getVideoCallToasterId';
import {
    TOGGLE_SCREEN_MODE,
    VIDEO_CALL_MICROPHONE_STATUS,
    VIDEO_CALL_SCREEN_MODE,
    VideoCallMicrophoneStatus,
    VideoCallScreenMode,
} from './utils/video-call-dialog-config';
import { VideoCallsGateway } from './video-calls.gateway';

/**
 * The call the installer is in, one at a time, and its window.
 * Logging out from the side menu ends it, so the client is not left waiting in the call.
 */
@Injectable({ providedIn: 'root' })
export class VideoCallSessionStore {
    private readonly provider = inject(VIDEO_CALL_PROVIDER);
    private readonly gateway = inject(VideoCallsGateway);
    private readonly dialog = inject(MatDialog);
    private readonly overlay = inject(Overlay);
    private readonly toaster = useToaster();

    private readonly currentSiteId = signal<SiteId | null>(null);
    private readonly currentScreenMode = signal<VideoCallScreenMode>(VIDEO_CALL_SCREEN_MODE.FULLSCREEN);
    private readonly currentMicrophone = signal<VideoCallMicrophoneStatus>(VIDEO_CALL_MICROPHONE_STATUS.ON);
    private dialogRef?: MatDialogRef<unknown>;
    private joining?: Promise<void>;
    /** Logging out leaves the application, so this is never reset. */
    private signingOut = false;

    /** The installation of the current call, or null. */
    readonly siteId = this.currentSiteId.asReadonly();
    readonly screenMode = this.currentScreenMode.asReadonly();
    readonly microphone = this.currentMicrophone.asReadonly();

    constructor() {
        inject(SessionStore).beforeSignOut(() => {
            this.signingOut = true;
            return this.hangUp();
        });
    }

    join(call: VideoCall): Promise<void> {
        this.joining ??= this.open(call).finally(() => (this.joining = undefined));
        return this.joining;
    }

    private async open({ siteId, url, ownerToken }: VideoCall): Promise<void> {
        // Loaded on demand, since the window's components read this store and a static import would be a cycle.
        // Loaded first, so a failed load leaves no current call.
        const { VideoCallModalComponent } = await import('./components/video-call-modal/video-call-modal.component');
        // A call is already open, or the installer is signing out.
        if (this.currentSiteId() !== null || this.signingOut) return;

        this.toaster.dismiss(getVideoCallToasterId(siteId));
        this.currentSiteId.set(siteId);

        const data: VideoCallModalData = { url, ownerToken };
        const dialogRef = this.dialog.open(VideoCallModalComponent, {
            disableClose: true,
            // Browser back or forward would close the window and leave the call running with no controls.
            closeOnNavigation: false,
            panelClass: 'video-call-modal',
            backdropClass: 'video-call-modal-backdrop',
            scrollStrategy: this.overlay.scrollStrategies.noop(),
            width: '100%',
            maxWidth: '95vw',
            height: '95vh',
            data,
        });
        this.dialogRef = dialogRef;
        // A window closing after "end and join" must not forget the next one.
        dialogRef.afterClosed().subscribe(() => {
            if (this.dialogRef === dialogRef) this.dialogRef = undefined;
        });
    }

    /** Rejects, without joining, when the current call cannot be ended: the caller reports it. */
    async stopAndJoin(call: VideoCall): Promise<void> {
        await this.hangUp();
        await this.join(call);
    }

    /** Leaves the call, stops it for the client, then closes the window. Does nothing when no call is open. */
    async hangUp(): Promise<void> {
        const siteId = this.currentSiteId();
        if (siteId !== null) await this.end(() => this.gateway.stop(siteId));
    }

    /**
     * The call stopped elsewhere (socket `hungUp`): leaves it and closes its window.
     * The late `hungUp` of a call ended by "end and join" keeps the next one.
     */
    async ended(siteId: SiteId): Promise<void> {
        if (siteId === this.currentSiteId()) await this.end();
    }

    toggleScreenMode(): void {
        this.currentScreenMode.update((screenMode) => TOGGLE_SCREEN_MODE[screenMode]);
    }

    toggleMicrophone(): void {
        if (this.currentMicrophone() === VIDEO_CALL_MICROPHONE_STATUS.ON) {
            this.provider.turnOffMicrophone();
            this.currentMicrophone.set(VIDEO_CALL_MICROPHONE_STATUS.OFF);
        } else {
            this.provider.turnOnMicrophone();
            this.currentMicrophone.set(VIDEO_CALL_MICROPHONE_STATUS.ON);
        }
    }

    /** Resets the session at once, leaves the call, stops it when asked, then closes the window. */
    private async end(stop?: () => Promise<void>): Promise<void> {
        const dialogRef = this.dialogRef;
        this.dialogRef = undefined;
        this.reset();
        try {
            await this.provider.hungUpVideoCall();
        } finally {
            // Even when leaving failed, stopping the call frees the client, and the window closes.
            try {
                await stop?.();
            } finally {
                dialogRef?.close();
            }
        }
    }

    private reset(): void {
        this.currentSiteId.set(null);
        this.currentScreenMode.set(VIDEO_CALL_SCREEN_MODE.FULLSCREEN);
        this.currentMicrophone.set(VIDEO_CALL_MICROPHONE_STATUS.ON);
    }
}
